import React, { useState, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { useLocation, useParams } from "react-router-dom";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { GET_POLICIES_BY_CHECK_ID } from "../../../../apis/settingsApis/policy-apis/policyApis";
import EvidenceTableContainer from "../common/EvidenceTableContainer";
import ReusableTable from "../../../../components/ReusableTable/ReusableTable";
import EvidenceLoadingSkeleton from "../common/EvidenceLoadingSkeleton";
import PolicyPreviewDialog from "./PolicyPreviewDialog";

const PolicyEvidenceTab = ({ checkDetails, isAuditorView = false, rightAction = null }) => {
  const location = useLocation();
  const { auditId: paramAuditId } = useParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);

  // Extract activeCheckId from checkDetails
  const activeCheckId = checkDetails?.active_check_id
    ? parseInt(checkDetails.active_check_id)
    : null;
  const auditId = location.state?.audit?.id || paramAuditId;

  // Fetch policies for this specific check
  const { data: policiesData, loading } = useQuery(GET_POLICIES_BY_CHECK_ID, {
    variables: { activeCheckId: activeCheckId, audit_id: auditId },
    skip: !activeCheckId || (isAuditorView && !auditId),
    fetchPolicy: "cache-and-network",
  });

  const policies = useMemo(() => {
    return policiesData?.getPoliciesByCheckId || [];
  }, [policiesData]);

  // Search filtering
  const searchedPolicies = useMemo(() => {
    if (!searchTerm) return policies;

    const query = searchTerm.toLowerCase();
    return policies.filter(
      (policy) =>
        policy.policyDisplayName?.toLowerCase().includes(query) ||
        policy.policyName?.toLowerCase().includes(query) ||
        policy.fileName?.toLowerCase().includes(query)
    );
  }, [policies, searchTerm]);

  const handleViewPolicy = (policy) => {
    setSelectedPolicy(policy);
    setPreviewDialogOpen(true);
  };

  const columns = [
    { label: "Policy Name", field: "policyDisplayName", align: "left", width: "45%" },
    { label: "File Name", field: "fileName", align: "left", width: "45%" },
    { label: "Version", field: "fileVersion", align: "left", width: "15%" },
    { label: "", field: "actions", align: "right", width: "5%" },
  ];

  const renderCell = (row, column) => {
    switch (column.field) {
      case "policyDisplayName":
        return (
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              fontSize: "16px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.policyDisplayName || row.policyName || "Unnamed Policy"}
          </Typography>
        );

      case "fileName":
        return (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontSize: "14px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.fileName || row.policyFileName || "N/A"}
          </Typography>
        );

      case "fileVersion":
        return (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontSize: "14px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              pl: 0.5,
            }}
          >
            v{row.fileVersion || "N/A"}
          </Typography>
        );

      case "actions":
        return (
          <Tooltip title="View policy">
            <IconButton
              size="small"
              onClick={() => handleViewPolicy(row)}
              color="primary"
            >
              <RemoveRedEyeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );

      default:
        return (
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row[column.field]}
          </Typography>
        );
    }
  };

  if (loading) {
    return <EvidenceLoadingSkeleton />;
  }

  return (
    <>
      <EvidenceTableContainer
        title="Policy Evidence"
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        clearFilters={() => setSearchTerm("")}
        filteredCount={searchedPolicies.length}
        countLabel={`${searchedPolicies.length} ${
          searchedPolicies.length === 1 ? "policy" : "policies"
        } found`}
        rightAction={rightAction}
      >
        <ReusableTable
          columns={columns}
          data={searchedPolicies}
          renderCell={renderCell}
          height="auto"
          showPagination={true}
          rowsPerPageOptions={[10, 25, 50]}
          emptyMessage="No policy evidence found for this check"
        />
      </EvidenceTableContainer>

      <PolicyPreviewDialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        policy={selectedPolicy}
      />
    </>
  );
};

export default PolicyEvidenceTab;
